/**
 * Insurance Lead Queue Types
 *
 * Types for the Insurance Lead Queue module.
 * These types mirror the Lambda types in lambda/insurance/shared/insurance-types.ts
 */

// ========================================
// LEAD STATUS & MATCH TYPES
// ========================================

/**
 * Lead status progression:
 * - unclaimed: New lead ready for agents to claim
 * - needs_manual_review: DealerBuilt match failed, needs manual data entry
 * - claimed: Agent is working the lead
 * - completed: Agent finished working the lead (success)
 * - discarded: Lead was discarded (duplicate, bad data, etc.)
 */
export type LeadStatus = 'unclaimed' | 'needs_manual_review' | 'claimed' | 'completed' | 'discarded';

/**
 * Match confidence from DealerBuilt lookup:
 * - exact: Stock number + name + phone all matched
 * - partial: Stock number matched but name/phone mismatch (possible spouse)
 * - none: No match found in DealerBuilt
 */
export type MatchConfidence = 'exact' | 'partial' | 'none';

// ========================================
// SOURCE DATA (FROM VINSOLUTIONS)
// ========================================

export interface LeadSourceData {
  customerName: string;
  email?: string;
  cellPhone?: string;
  daytimePhone?: string;
  eveningPhone?: string;
  primaryPhone: string;
  address?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  stockNumber: string;
  dealership: string;
  soldDatetime: string;
  importedAt: string;
  sourceEmailId?: string;
}

// ========================================
// DEALERBUILT MATCHED DATA
// ========================================

export interface DealerBuiltMatchData {
  dealNumber: number;
  guestSeq?: number;
  salesperson?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  homePhone?: string;
  workPhone?: string;
  cellPhone?: string;
  address1?: string;
  address2?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  driversLicense?: string;
  dlState?: string;
  dlExpiration?: string;
  vehicleYear?: string;
  vehicleMake?: string;
  vehicleModel?: string;
  vin?: string;
  vehicleType?: string;
  vehicleColor?: string;
  insuranceCompany?: string;
  insuranceAgent?: string;
  policyNumber?: string;
  policyEffectiveDate?: string;
  policyExpirationDate?: string;
  lenderCode?: string;
  lenderName?: string;
  lenderAddress?: string;
  lenderCsz?: string;
  secondLienHolder?: string;
  secondLienHolderAddress?: string;
  secondLienHolderCsz?: string;
}

export interface MatchDetails {
  matchedFields: string[];
  mismatchedFields: string[];
  matchNotes?: string;
}

// ========================================
// MANUAL ENTRY DATA
// ========================================

export interface ManualEntryData {
  vehicleYear?: string;
  vehicleMake?: string;
  vehicleModel?: string;
  vin?: string;
  driversLicense?: string;
  dlState?: string;
  insuranceCompany?: string;
  insuranceAgent?: string;
  policyNumber?: string;
  policyEffectiveDate?: string;
  policyExpirationDate?: string;
}

// ========================================
// MAIN LEAD TYPE
// ========================================

export interface InsuranceLead {
  leadId: string;
  stockNumber: string;
  status: LeadStatus;
  createdAt: string;
  updatedAt: string;
  locationId: number;
  locationName: string;
  sourceData: LeadSourceData;
  matchConfidence: MatchConfidence;
  matchedData?: DealerBuiltMatchData;
  matchDetails?: MatchDetails;
  manualEntry?: ManualEntryData;
  claimedBy?: string;
  claimedByName?: string;
  claimedAt?: string;
  completedAt?: string;
  completedBy?: string;
  completedByName?: string;
  outcome?: 'quoted_purchased' | 'quoted_not_purchased' | 'bad_lead' | 'purchased' | 'not_purchased';
  discardedBy?: string;
  discardedByName?: string;
  discardedAt?: string;
  discardReason?: string;
  notes?: string;
  ttl?: number;
}

// ========================================
// API REQUEST/RESPONSE TYPES
// ========================================

export interface LeadQueryParams {
  status?: LeadStatus;
  locationId?: number;
  myLeadsOnly?: boolean;
  limit?: number;
  nextToken?: string;
}

export interface LeadUpdateRequest {
  notes?: string;
  status?: 'completed' | 'discarded';
  outcome?: 'quoted_purchased' | 'quoted_not_purchased' | 'bad_lead';
  discardReason?: string;
  manualEntry?: ManualEntryData;
}

export interface InsuranceStats {
  totalUnclaimed: number;
  totalNeedsReview: number;
  totalClaimed: number;
  totalCompletedToday: number;
  totalDiscardedToday: number;
  byLocation: Array<{
    locationId: number;
    locationName: string;
    unclaimed: number;
    claimed: number;
  }>;
  agentWorkload?: Array<{
    userId: string;
    userName: string;
    claimedCount: number;
    completedToday: number;
  }>;
}

export interface LeadsResponse {
  leads: InsuranceLead[];
  nextToken?: string;
  count: number;
}

// ========================================
// UI HELPER TYPES
// ========================================

export interface LeadStatusConfig {
  label: string;
  color: string;
  bgColor: string;
  description: string;
}

export const LEAD_STATUS_CONFIG: Record<LeadStatus, LeadStatusConfig> = {
  unclaimed: {
    label: 'Unclaimed',
    color: 'text-blue-700',
    bgColor: 'bg-blue-100',
    description: 'Ready to be claimed',
  },
  needs_manual_review: {
    label: 'Needs Review',
    color: 'text-yellow-700',
    bgColor: 'bg-yellow-100',
    description: 'No DealerBuilt match - requires manual data entry',
  },
  claimed: {
    label: 'Claimed',
    color: 'text-purple-700',
    bgColor: 'bg-purple-100',
    description: 'Being worked by an agent',
  },
  completed: {
    label: 'Completed',
    color: 'text-green-700',
    bgColor: 'bg-green-100',
    description: 'Successfully processed',
  },
  discarded: {
    label: 'Discarded',
    color: 'text-gray-700',
    bgColor: 'bg-gray-100',
    description: 'Discarded - not actionable',
  },
};

export interface MatchConfidenceConfig {
  label: string;
  color: string;
  bgColor: string;
  icon: 'check' | 'warning' | 'x';
}

export const MATCH_CONFIDENCE_CONFIG: Record<MatchConfidence, MatchConfidenceConfig> = {
  exact: {
    label: 'Exact Match',
    color: 'text-green-700',
    bgColor: 'bg-green-100',
    icon: 'check',
  },
  partial: {
    label: 'Partial Match',
    color: 'text-yellow-700',
    bgColor: 'bg-yellow-100',
    icon: 'warning',
  },
  none: {
    label: 'No Match',
    color: 'text-red-700',
    bgColor: 'bg-red-100',
    icon: 'x',
  },
};
